import { addDimensionOffset, dimensionNumber } from '../multiPositionFields';

const roles = {
  lm_b_value: 'lmWidth', lm_h_value: 'lmHeight',
  rm_b_value: 'ramWidth', rm_h_value: 'ramHeight',
};

// This extension adapts the existing frame templates and their legacy calculations.
// It supplies field semantics and lifecycle hooks, never a separate list of inputs.
export default {
  positionSelector: 'input.sm_value, select.s_mass_dichtb_value',
  engineProperties: [
    'isXLVariant', 'isHeightGreaterThanWidth', 'isHeightForKaempfer', 'isWidthForKaempfer',
    'ramBOverridden', 'ramBOverrideValue', 'ramBStandardValue', 'ramHOverridden', 'ramHOverrideValue', 'ramHStandardValue',
    'zamBOverridden', 'zamBOverrideValue', 'zamBStandardValue', 'zamHOverridden', 'zamHOverrideValue', 'zamHStandardValue',
  ],
  describeField({ control, step }) {
    let role = Object.entries(roles).find(([className]) => control.classList.contains(className))?.[1];
    if (!role && /^(maße?|ram maße?)$/i.test(step.name)) {
      if (control.classList.contains('x_value')) role = 'width';
      if (control.classList.contains('y_value')) role = 'height';
    }
    return role ? {
      role, axis: role.toLowerCase().includes('height') ? 'height' : 'width', positive: true, alwaysVisible: true,
    } : {};
  },
  extendSchema({ schema, templates }) {
    const { fields, errors } = schema;
    if (!fields.some(field => ['width', 'ramWidth'].includes(field.role))
      || !fields.some(field => ['height', 'ramHeight'].includes(field.role))) {
      errors.push('Die erforderlichen Breiten- und Höhenfelder konnten nicht eindeutig ermittelt werden.');
    }
    for (const role of [...Object.values(roles), 'width', 'height']) {
      if (fields.filter(field => field.role === role).length > 1) {
        errors.push('Die Maßfelder dieses Produkts sind nicht eindeutig. Bitte die Einzelposition verwenden.');
      }
    }
    for (const [sourceRole, targetRole, selector] of [
      ['lmWidth', 'ramWidth', '.rm_b_add_value'], ['lmHeight', 'ramHeight', '.rm_h_add_value'],
    ]) {
      const source = fields.find(field => field.role === sourceRole);
      if (!source) continue;
      const target = fields.find(field => field.role === targetRole);
      const additions = [...templates.values()].flatMap(root => Array.from(root.querySelectorAll(selector)));
      const offset = additions.length === 1 ? dimensionNumber(additions[0].textContent) : null;
      if (!target || offset === null) {
        errors.push('Der LM/RAM-Zuschlag fehlt oder ist nicht eindeutig. Bitte die Einzelposition verwenden.');
        continue;
      }
      schema.calculations.push({
        sourceKeys: [source.key], targetKey: target.key, warnOnOverride: true,
        calculate: values => addDimensionOffset(values[source.key]?.value, offset),
      });
    }
  },
  resetEngine({ engine }) {
    engine.isXLVariant = engine.searchTextArticle.includes('-XL');
    engine.isHeightForKaempfer = false;
    engine.isWidthForKaempfer = false;
  },
  prepareEvaluation({ engine }) { engine.calculateSMASS(); },
  beforePrice({ engine }) {
    engine.checkAndManageXLCheckboxes(true);
    engine.isXLVariant = engine.searchTextArticle.includes('-XL')
      || Array.from(engine.$refs.dynamicContent.querySelectorAll('input[type="checkbox"]')).some(control =>
        control.checked && Array.from(control.classList).some(name => name.toLowerCase().includes('xl'))
        && !engine.isStepHidden(control.closest('[id^="s_"]')));
    engine.validateKaempferMasse();
  },
  isPriceOnRequest: price => price <= 1,
  validate({ engine, schema, availability }) {
    const errors = [];
    if (!engine.selectedPrice) errors.push('Für dieses Produkt fehlt eine Preisliste.');
    for (const acceptedRoles of [['width', 'ramWidth'], ['height', 'ramHeight']]) {
      const dimension = schema.fields.find(field => acceptedRoles.includes(field.role));
      if (!dimension || !availability[dimension.key]?.visible) {
        errors.push('Die erforderlichen Maßfelder konnten nicht vollständig ausgewertet werden. Bitte die Einzelposition verwenden.');
      }
    }
    return errors;
  },
};
